import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { Card } from '../Card';
import { TextCut } from '../TextCut';
import { Typography } from '../typography';

import { listItemVariants } from './listItemVariants';

import type { CardProps } from '../Card';
import type { TypographyProps } from '../typography';
import type { VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';

import s from './ListItem.module.css';

type ListItemVariant = NonNullable<VariantProps<typeof listItemVariants>['variant']>;

const variantColorMap = {
  accent: 'accent-act',
  alt: 'accent-alt',
  default: 'white',
  selected: 'black-body',
  success: 'success',
  warning: 'danger',
} as const satisfies Record<ListItemVariant, NonNullable<TypographyProps['color']>>;

const getColorVariant = (
  variant: keyof typeof variantColorMap,
  useVariantColors: boolean = false,
): NonNullable<TypographyProps['color']> => {
  if (variant === 'selected' || useVariantColors) {
    return variantColorMap[variant];
  }

  return 'white';
};

export type ListItemProps = Omit<CardProps, 'background' | 'prefix' | 'suffix' | 'title'> & {
  /** Main title text */
  title: ReactNode;
  /** Additional content displayed on the bottom of the ListItem */
  additionalContent?: ReactNode;
  /** Number of lines to show for children content before truncating with ellipsis */
  childrenLinesCount?: number;
  /** Content displayed before the content block */
  contentPrefix?: ReactNode;
  /** Content displayed after the content block */
  contentSuffix?: ReactNode;
  /** If true, content area takes full width available */
  fullWidthContent?: boolean;
  /** Center content by applying equal padding based on larger side (prefix or suffix) */
  isCentered?: boolean;
  /** Content displayed before title (e.g., avatar, icon) */
  prefix?: ReactNode;
  /** Spacing level for prefix: 1 = largest, 2 = medium, 3 = smallest */
  prefixSpacing?: 1 | 2 | 3;
  /** Content displayed on the right side */
  suffix?: ReactNode;
  /** Spacing level for suffix: 1 = largest, 2 = medium, 3 = smallest */
  suffixSpacing?: 1 | 2 | 3;
  /** Use variant colors for title and suffix instead of default colors */
  useVariantColors?: boolean;
  /** Visual variant of the ListItem */
  variant?: ListItemVariant;
};

export const ListItem = ({
  additionalContent,
  children,
  childrenLinesCount = 1,
  className,
  contentPrefix,
  contentSuffix,
  fullWidthContent = false,
  isCentered = false,
  prefix,
  prefixSpacing = 3,
  suffix,
  suffixSpacing = 3,
  title,
  useVariantColors = false,
  variant = 'default',
  ...props
}: ListItemProps) => {
  const [paddingValues, setPaddingValues] = useState<{ end: number; start: number }>({ end: 0, start: 0 });
  const prefixRef = useRef<HTMLDivElement>(null);
  const suffixRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isCentered) {
      return;
    }

    const calculatePadding = () => {
      const prefixWidth = prefixRef.current?.getBoundingClientRect().width ?? 0;
      const suffixWidth = suffixRef.current?.getBoundingClientRect().width ?? 0;

      if (prefixWidth === suffixWidth) {
        setPaddingValues({ end: 0, start: 0 });

        return;
      }

      const difference = Math.abs(prefixWidth - suffixWidth);

      setPaddingValues({
        end: suffixWidth < prefixWidth ? difference : 0,
        start: suffixWidth > prefixWidth ? difference : 0,
      });
    };

    const observer = new ResizeObserver(calculatePadding);

    if (prefixRef.current) {
      observer.observe(prefixRef.current);
    }

    if (suffixRef.current) {
      observer.observe(suffixRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [isCentered, prefix, suffix]);

  const color = getColorVariant(variant, useVariantColors);

  return (
    <Card className={clsx(listItemVariants({ prefixSpacing, suffixSpacing, variant }), className)} {...props}>
      <div className={s.mainRow}>
        {Boolean(prefix) && (
          <Typography ref={prefixRef} className={s.prefix} color={color} component="span" variant="inset-paragraph">
            {prefix}
          </Typography>
        )}

        <div
          style={
            isCentered ?
              {
                paddingInlineEnd: `${paddingValues.end}px`,
                paddingInlineStart: `${paddingValues.start}px`,
              }
            : undefined
          }
          className={clsx(s.contentWrapper, isCentered && s['contentWrapper--centered'])}
        >
          {Boolean(contentPrefix) && (
            <Typography className={s.contentPrefix} color={color} component="span" variant="inset-paragraph">
              {contentPrefix}
            </Typography>
          )}

          <div className={clsx(s.content, fullWidthContent && s['content--fullWidth'])}>
            <Typography className={s.title} color={color} component="span" variant="inset-paragraph" bold>
              <TextCut linesCount={1}>{title}</TextCut>
            </Typography>

            {Boolean(children) && (
              <Typography className={s.children} color="grey-light" component="span" variant="inset-paragraph">
                <TextCut linesCount={childrenLinesCount}>{children} </TextCut>
              </Typography>
            )}
          </div>

          {Boolean(contentSuffix) && (
            <Typography className={s.contentSuffix} color={color} component="span" variant="inset-paragraph">
              {contentSuffix}
            </Typography>
          )}
        </div>

        {Boolean(suffix) && (
          <Typography ref={suffixRef} className={s.suffix} color={color} component="span" variant="inset-paragraph">
            {suffix}
          </Typography>
        )}
      </div>

      {Boolean(additionalContent) && (
        <Typography className={s.additionalContent} color={color} component="div" variant="inset-paragraph">
          {additionalContent}
        </Typography>
      )}
    </Card>
  );
};
